/**
 * @fileoverview AI Chat Bubble component — floating ask-AI widget
 * @module ctl-ask-ai
 * @version 1.0.0
 *
 * @description
 * A fixed-position floating chat bubble that opens a chat window connected to
 * an AWS serverless LLM microservice endpoint. Compose messages, stream
 * responses, and maintain conversation history — all with zero external deps.
 *
 * @example
 * <!-- Basic usage — drop on any page -->
 * <ctl-ask-ai endpoint="https://api.example.com/ask"></ctl-ask-ai>
 *
 * @example
 * <!-- Custom title and greeting -->
 * <ctl-ask-ai
 *   endpoint="https://api.example.com/ask"
 *   title="Support Chat"
 *   greeting="Hi! How can I help you today?"
 *   placeholder="Ask me anything…"
 *   position="bottom-left"
 * ></ctl-ask-ai>
 *
 * @fires chat-open        - Chat window opened. No detail.
 * @fires chat-close       - Chat window closed. No detail.
 * @fires message-send     - User sent a message. Detail: { text }
 * @fires response-receive - AI response received. Detail: { text }
 * @fires chat-error       - API error occurred. Detail: { message, status }
 */

const template = document.createElement("template");

template.innerHTML = `
<style>
  /* ── Host / Reset ──────────────────────────────────────────────────── */
  :host {
    --ctl-ask-ai-bubble-size: 56px;
    --ctl-ask-ai-bubble-bg: var(--ctl-color-brand);
    --ctl-ask-ai-bubble-hover: var(--ctl-color-brand-hover);
    --ctl-ask-ai-bubble-text: var(--ctl-color-white);
    --ctl-ask-ai-shadow: var(--ctl-shadow-lg, 0 10px 15px rgba(0,0,0,.15));
    --ctl-ask-ai-panel-width: 360px;
    --ctl-ask-ai-panel-height: 520px;
    --ctl-ask-ai-panel-bg: var(--ctl-color-surface);
    --ctl-ask-ai-panel-border: var(--ctl-color-border);
    --ctl-ask-ai-header-bg: var(--ctl-color-brand);
    --ctl-ask-ai-header-text: var(--ctl-color-white);
    --ctl-ask-ai-inset: var(--ctl-space-5, 24px);
    --ctl-ask-ai-radius: var(--ctl-radius-lg, 16px);

    display: contents;
    font-family: var(--ctl-font-sans, system-ui, -apple-system, Segoe UI, Roboto, Arial);
  }

  /* ── Bubble trigger button ─────────────────────────────────────────── */
  .bubble {
    position: fixed;
    bottom: var(--ctl-ask-ai-inset);
    right: var(--ctl-ask-ai-inset);
    width: var(--ctl-ask-ai-bubble-size);
    height: var(--ctl-ask-ai-bubble-size);
    border-radius: var(--ctl-radius-full, 9999px);
    background: var(--ctl-ask-ai-bubble-bg);
    color: var(--ctl-ask-ai-bubble-text);
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: var(--ctl-ask-ai-shadow);
    z-index: var(--ctl-z-notification, 10000);
    transition:
      background var(--ctl-transition-fast, 120ms ease),
      transform var(--ctl-transition-fast, 120ms ease),
      box-shadow var(--ctl-transition-fast, 120ms ease);
  }

  :host([position="bottom-left"]) .bubble {
    right: auto;
    left: var(--ctl-ask-ai-inset);
  }

  .bubble:hover {
    background: var(--ctl-ask-ai-bubble-hover);
    transform: scale(1.06);
    box-shadow: var(--ctl-shadow-xl, 0 25px 50px rgba(0,0,0,.25));
  }

  .bubble:focus-visible {
    outline: 3px solid var(--ctl-ask-ai-bubble-bg);
    outline-offset: 3px;
  }

  .bubble:active {
    transform: scale(0.96);
  }

  /* ── SVG icons ─────────────────────────────────────────────────────── */
  .icon {
    width: 24px;
    height: 24px;
    fill: currentColor;
    pointer-events: none;
    flex-shrink: 0;
  }

  .icon-close { display: none; }

  :host([open]) .icon-chat  { display: none; }
  :host([open]) .icon-close { display: block; }

  /* ── Panel ─────────────────────────────────────────────────────────── */
  .panel {
    position: fixed;
    bottom: calc(var(--ctl-ask-ai-inset) + var(--ctl-ask-ai-bubble-size) + var(--ctl-space-3, 12px));
    right: var(--ctl-ask-ai-inset);
    width: var(--ctl-ask-ai-panel-width);
    height: var(--ctl-ask-ai-panel-height);
    max-width: calc(100vw - var(--ctl-ask-ai-inset) * 2);
    max-height: calc(100vh - var(--ctl-ask-ai-inset) * 2 - var(--ctl-ask-ai-bubble-size) - var(--ctl-space-3, 12px));
    background: var(--ctl-ask-ai-panel-bg);
    border: 1px solid var(--ctl-ask-ai-panel-border);
    border-radius: var(--ctl-ask-ai-radius);
    box-shadow: var(--ctl-shadow-dialog, 0 20px 60px rgba(0,0,0,.25));
    display: none;
    flex-direction: column;
    overflow: hidden;
    z-index: calc(var(--ctl-z-notification, 10000) - 1);
    transform-origin: bottom right;
  }

  :host([position="bottom-left"]) .panel {
    right: auto;
    left: var(--ctl-ask-ai-inset);
    transform-origin: bottom left;
  }

  :host([open]) .panel {
    display: flex;
    animation: panelIn var(--ctl-transition-slow, 300ms ease) both;
  }

  @keyframes panelIn {
    from { opacity: 0; transform: scale(0.92) translateY(8px); }
    to   { opacity: 1; transform: scale(1)    translateY(0); }
  }

  /* ── Panel header ──────────────────────────────────────────────────── */
  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--ctl-space-3, 12px) var(--ctl-space-4, 16px);
    background: var(--ctl-ask-ai-header-bg);
    color: var(--ctl-ask-ai-header-text);
    flex-shrink: 0;
  }

  .header-title {
    display: flex;
    align-items: center;
    gap: var(--ctl-space-2, 8px);
    font-size: var(--ctl-font-size-base, 14px);
    font-weight: var(--ctl-font-weight-semibold, 600);
    margin: 0;
  }

  .header-close {
    background: none;
    border: none;
    cursor: pointer;
    color: inherit;
    padding: var(--ctl-space-1, 4px);
    border-radius: var(--ctl-radius-sm, 4px);
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    min-height: 28px;
    opacity: 0.85;
    transition: opacity var(--ctl-transition-fast, 120ms ease);
  }

  .header-close:hover { opacity: 1; }
  .header-close:focus-visible {
    outline: 2px solid var(--ctl-ask-ai-header-text);
    outline-offset: 2px;
  }

  /* ── Message thread area ───────────────────────────────────────────── */
  .thread {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: var(--ctl-space-3, 12px) var(--ctl-space-4, 16px);
    display: flex;
    flex-direction: column;
    gap: var(--ctl-space-2, 8px);
    scroll-behavior: smooth;
  }

  /* Custom scrollbar */
  .thread::-webkit-scrollbar { width: 6px; }
  .thread::-webkit-scrollbar-track { background: transparent; }
  .thread::-webkit-scrollbar-thumb {
    background: var(--ctl-color-border);
    border-radius: 3px;
  }
  .thread::-webkit-scrollbar-thumb:hover {
    background: var(--ctl-color-text-secondary);
  }

  /* ── Message bubbles ───────────────────────────────────────────────── */
  .msg {
    display: flex;
    flex-direction: column;
    max-width: 82%;
    word-break: break-word;
    overflow-wrap: anywhere;
  }

  .msg--user  { align-self: flex-end; align-items: flex-end; }
  .msg--assistant { align-self: flex-start; align-items: flex-start; }

  .msg__bubble {
    padding: var(--ctl-space-2, 8px) var(--ctl-space-3, 12px);
    border-radius: var(--ctl-space-4, 16px);
    font-size: var(--ctl-font-size-base, 14px);
    line-height: var(--ctl-leading-normal, 1.5);
    white-space: pre-wrap;
  }

  .msg--user .msg__bubble {
    background: var(--ctl-color-brand);
    color: var(--ctl-color-on-brand);
    border-bottom-right-radius: var(--ctl-radius-sm, 4px);
  }

  .msg--assistant .msg__bubble {
    background: var(--ctl-color-surface-secondary);
    color: var(--ctl-color-text);
    border-bottom-left-radius: var(--ctl-radius-sm, 4px);
  }

  .msg__time {
    font-size: var(--ctl-font-size-xs, 11px);
    color: var(--ctl-color-text-secondary);
    margin-top: var(--ctl-space-0-5, 2px);
    padding: 0 var(--ctl-space-1, 4px);
  }

  /* ── Typing indicator ──────────────────────────────────────────────── */
  .typing {
    display: none;
    align-self: flex-start;
    padding: var(--ctl-space-2, 8px) var(--ctl-space-3, 12px);
    background: var(--ctl-color-surface-secondary);
    border-radius: var(--ctl-space-4, 16px);
    border-bottom-left-radius: var(--ctl-radius-sm, 4px);
    gap: var(--ctl-space-1, 4px);
  }

  :host([loading]) .typing { display: flex; }

  .typing__dot {
    width: 7px;
    height: 7px;
    border-radius: var(--ctl-radius-full, 9999px);
    background: var(--ctl-color-text-secondary);
    animation: typingBounce 1.2s infinite ease-in-out;
  }
  .typing__dot:nth-child(2) { animation-delay: 0.2s; }
  .typing__dot:nth-child(3) { animation-delay: 0.4s; }

  @keyframes typingBounce {
    0%, 80%, 100% { transform: scale(0.7); opacity: 0.4; }
    40%           { transform: scale(1);   opacity: 1;   }
  }

  /* ── Error banner ──────────────────────────────────────────────────── */
  .error-banner {
    display: none;
    align-items: center;
    gap: var(--ctl-space-2, 8px);
    padding: var(--ctl-space-2, 8px) var(--ctl-space-3, 12px);
    background: var(--ctl-color-danger-surface);
    color: var(--ctl-color-danger-text);
    font-size: var(--ctl-font-size-sm, 12px);
    border-top: 1px solid var(--ctl-color-danger-border);
    flex-shrink: 0;
  }

  :host([error]) .error-banner { display: flex; }

  /* ── Input row ─────────────────────────────────────────────────────── */
  .input-row {
    display: flex;
    align-items: center;
    gap: var(--ctl-space-2, 8px);
    padding: var(--ctl-space-3, 12px) var(--ctl-space-4, 16px);
    border-top: 1px solid var(--ctl-ask-ai-panel-border);
    background: var(--ctl-ask-ai-panel-bg);
    flex-shrink: 0;
  }

  .input-field {
    flex: 1;
    padding: var(--ctl-space-2, 8px) var(--ctl-space-3, 12px);
    font-size: var(--ctl-font-size-base, 14px);
    font-family: inherit;
    border: 1px solid var(--ctl-ask-ai-panel-border);
    border-radius: var(--ctl-radius-full, 9999px);
    background: var(--ctl-ask-ai-panel-bg);
    color: var(--ctl-color-text);
    resize: none;
    min-height: 44px;
    max-height: 120px;
    line-height: var(--ctl-leading-normal, 1.5);
    outline: none;
    transition: border-color var(--ctl-transition-fast, 120ms ease);
  }

  .input-field:focus {
    border-color: var(--ctl-color-brand);
  }

  .input-field:disabled { opacity: var(--ctl-opacity-disabled, 0.5); }

  .send-btn {
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    border-radius: var(--ctl-radius-full, 9999px);
    background: var(--ctl-color-brand);
    color: var(--ctl-color-on-brand, var(--ctl-color-white));
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition:
      background var(--ctl-transition-fast, 120ms ease),
      transform var(--ctl-transition-fast, 120ms ease);
  }

  .send-btn:hover:not(:disabled) {
    background: var(--ctl-color-brand-hover);
  }

  .send-btn:focus-visible {
    outline: 3px solid var(--ctl-color-brand);
    outline-offset: 2px;
  }

  .send-btn:disabled {
    opacity: var(--ctl-opacity-disabled, 0.5);
    cursor: not-allowed;
  }

  .send-btn:active:not(:disabled) { transform: scale(0.92); }

  /* ── Powered-by footer ─────────────────────────────────────────────── */
  .footer {
    text-align: center;
    font-size: var(--ctl-font-size-xs, 11px);
    color: var(--ctl-color-text-secondary);
    padding: var(--ctl-space-1, 4px) var(--ctl-space-4, 16px) var(--ctl-space-2, 8px);
    flex-shrink: 0;
  }
</style>

<!-- Floating trigger bubble -->
<button
  class="bubble"
  part="bubble"
  type="button"
  aria-label="Open AI chat"
  aria-haspopup="dialog"
  aria-expanded="false"
>
  <!-- Chat icon -->
  <svg class="icon icon-chat" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z"/>
  </svg>
  <!-- Close icon -->
  <svg class="icon icon-close" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
  </svg>
</button>

<!-- Chat panel -->
<div
  class="panel"
  part="panel"
  role="dialog"
  aria-label="AI chat"
  aria-modal="false"
>
  <!-- Header -->
  <div class="header" part="header">
    <h2 class="header-title" part="title">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z"/>
      </svg>
      Ask AI
    </h2>
    <button
      class="header-close"
      part="close-btn"
      type="button"
      aria-label="Close chat"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
      </svg>
    </button>
  </div>

  <!-- Messages -->
  <div class="thread" part="thread" role="log" aria-live="polite" aria-label="Chat messages"></div>

  <!-- Typing indicator -->
  <div class="typing" part="typing" aria-hidden="true">
    <span class="typing__dot"></span>
    <span class="typing__dot"></span>
    <span class="typing__dot"></span>
  </div>

  <!-- Error banner -->
  <div class="error-banner" part="error-banner" role="alert">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
    </svg>
    <span class="error-text"></span>
  </div>

  <!-- Input row -->
  <div class="input-row" part="input-row">
    <textarea
      class="input-field"
      part="input"
      rows="1"
      placeholder="Ask me anything…"
      aria-label="Message input"
    ></textarea>
    <button
      class="send-btn"
      part="send-btn"
      type="button"
      aria-label="Send message"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
      </svg>
    </button>
  </div>

  <!-- Footer -->
  <div class="footer" part="footer">Powered by AI</div>
</div>
`;

/**
 * AI Chat Bubble Web Component
 *
 * @class CtlAskAi
 * @extends HTMLElement
 *
 * @attr {string}  endpoint    - URL of the AWS serverless LLM API endpoint
 * @attr {string}  title       - Chat window title (default: "Ask AI")
 * @attr {string}  greeting    - Initial greeting from the assistant
 * @attr {string}  placeholder - Textarea placeholder (default: "Ask me anything…")
 * @attr {boolean} open        - Whether the chat panel is open
 * @attr {string}  position    - "bottom-right" (default) | "bottom-left"
 *
 * @fires chat-open        - Panel opened
 * @fires chat-close       - Panel closed
 * @fires message-send     - User sent message. detail: { text }
 * @fires response-receive - AI response received. detail: { text }
 * @fires chat-error       - API error. detail: { message, status }
 *
 * @csspart bubble     - Floating trigger button
 * @csspart panel      - Chat window container
 * @csspart header     - Panel header bar
 * @csspart title      - Title element in header
 * @csspart close-btn  - Header close button
 * @csspart thread     - Message list container
 * @csspart typing     - Typing indicator
 * @csspart error-banner - Error message bar
 * @csspart input-row  - Input area container
 * @csspart input      - Textarea element
 * @csspart send-btn   - Send button
 * @csspart footer     - Powered-by footer
 *
 * @cssprop --ctl-ask-ai-bubble-size   - Bubble button size (default: 56px)
 * @cssprop --ctl-ask-ai-bubble-bg     - Bubble background color
 * @cssprop --ctl-ask-ai-bubble-hover  - Bubble hover background color
 * @cssprop --ctl-ask-ai-panel-width   - Chat panel width (default: 360px)
 * @cssprop --ctl-ask-ai-panel-height  - Chat panel height (default: 520px)
 * @cssprop --ctl-ask-ai-header-bg     - Header background color
 * @cssprop --ctl-ask-ai-inset         - Distance from viewport edge (default: 24px)
 * @cssprop --ctl-ask-ai-radius        - Panel border radius (default: 16px)
 */
export class CtlAskAi extends HTMLElement {
  static get observedAttributes() {
    return ["endpoint", "title", "greeting", "placeholder", "open", "position"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));

    this._bubble     = this.shadowRoot.querySelector(".bubble");
    this._panel      = this.shadowRoot.querySelector(".panel");
    this._thread     = this.shadowRoot.querySelector(".thread");
    this._input      = this.shadowRoot.querySelector(".input-field");
    this._sendBtn    = this.shadowRoot.querySelector(".send-btn");
    this._closeBtn   = this.shadowRoot.querySelector(".header-close");
    this._titleEl    = this.shadowRoot.querySelector(".header-title");
    this._errorText  = this.shadowRoot.querySelector(".error-text");

    /** @type {Array<{role: string, content: string}>} Conversation history for context */
    this._history = [];
    this._abortCtrl = null;

    this._onBubbleClick  = this._onBubbleClick.bind(this);
    this._onCloseClick   = this._onCloseClick.bind(this);
    this._onSend         = this._onSend.bind(this);
    this._onKeyDown      = this._onKeyDown.bind(this);
    this._onInputInput   = this._onInputInput.bind(this);
    this._onDocKeyDown   = this._onDocKeyDown.bind(this);
  }

  connectedCallback() {
    this._bubble.addEventListener("click", this._onBubbleClick);
    this._closeBtn.addEventListener("click", this._onCloseClick);
    this._sendBtn.addEventListener("click", this._onSend);
    this._input.addEventListener("keydown", this._onKeyDown);
    this._input.addEventListener("input", this._onInputInput);
    document.addEventListener("keydown", this._onDocKeyDown);

    this._syncAttributes();

    // Show greeting when connected for the first time (if set)
    if (this._history.length === 0) {
      const greeting = this.getAttribute("greeting");
      if (greeting) {
        this._appendMessage("assistant", greeting);
        this._history.push({ role: "assistant", content: greeting });
      }
    }
  }

  disconnectedCallback() {
    this._bubble.removeEventListener("click", this._onBubbleClick);
    this._closeBtn.removeEventListener("click", this._onCloseClick);
    this._sendBtn.removeEventListener("click", this._onSend);
    this._input.removeEventListener("keydown", this._onKeyDown);
    this._input.removeEventListener("input", this._onInputInput);
    document.removeEventListener("keydown", this._onDocKeyDown);

    if (this._abortCtrl) {
      this._abortCtrl.abort();
      this._abortCtrl = null;
    }
  }

  attributeChangedCallback(name) {
    switch (name) {
      case "open":
        this._syncOpenState();
        break;
      case "title":
        this._syncTitle();
        break;
      case "placeholder":
        this._syncPlaceholder();
        break;
      case "position":
        // position is read directly from the attribute via CSS :host([position]) selectors;
        // no JS sync needed — the CSS cascade handles it automatically.
        break;
      // "endpoint" and "greeting" are intentionally init-time only:
      //   endpoint — read fresh on each _callEndpoint() call
      //   greeting — only shown once in connectedCallback; changing it later
      //              would require clearHistory() to take effect
      default:
        break;
    }
  }

  // ── Private helpers ──────────────────────────────────────────────────

  _syncAttributes() {
    this._syncOpenState();
    this._syncTitle();
    this._syncPlaceholder();
  }

  _syncOpenState() {
    const isOpen = this.hasAttribute("open");
    this._bubble.setAttribute("aria-expanded", String(isOpen));
    this._bubble.setAttribute("aria-label", isOpen ? "Close AI chat" : "Open AI chat");
    if (isOpen) {
      this._panel.removeAttribute("hidden");
      // Focus input for keyboard users
      requestAnimationFrame(() => this._input.focus());
    }
  }

  _syncTitle() {
    const title = this.getAttribute("title") || "Ask AI";

    // Preserve existing SVG icon and only update the title text node.
    const nodes = Array.from(this._titleEl.childNodes);
    let iconNode = null;
    let textNode = null;

    // Identify the first SVG element as the icon and the first text node as the title,
    // remove any other nodes to keep the structure predictable.
    for (const node of nodes) {
      if (
        node.nodeType === Node.ELEMENT_NODE &&
        node.tagName &&
        node.tagName.toLowerCase() === "svg" &&
        !iconNode
      ) {
        iconNode = node;
        continue;
      }
      if (node.nodeType === Node.TEXT_NODE && !textNode) {
        textNode = node;
        continue;
      }
      this._titleEl.removeChild(node);
    }

    // Ensure the icon (if any) is the first child.
    if (iconNode && this._titleEl.firstChild !== iconNode) {
      this._titleEl.insertBefore(iconNode, this._titleEl.firstChild);
    }

    // Prepare the text content. Add a leading space if there is an icon so that
    // the text does not sit flush against the SVG.
    const textContent = iconNode ? ` ${title}` : title;

    if (textNode) {
      textNode.textContent = textContent;
      // Ensure the text node is after the icon, if present.
      if (iconNode && textNode.previousSibling !== iconNode) {
        this._titleEl.insertBefore(textNode, iconNode.nextSibling);
      } else if (!iconNode && textNode.parentNode !== this._titleEl) {
        this._titleEl.appendChild(textNode);
      }
    } else {
      this._titleEl.appendChild(document.createTextNode(textContent));
    }
  }

  _syncPlaceholder() {
    this._input.placeholder = this.getAttribute("placeholder") || "Ask me anything…";
  }

  _onBubbleClick() {
    this.toggle();
  }

  _onCloseClick() {
    this.close();
  }

  _onDocKeyDown(e) {
    // Only respond to Escape for this specific instance when it is open,
    // preventing multiple ctl-ask-ai instances from all closing at once.
    if (e.key === "Escape" && this.hasAttribute("open")) {
      this.close();
      this._bubble.focus();
    }
  }

  _onKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      this._onSend();
    }
  }

  _onInputInput() {
    // Auto-grow textarea (max-height capped via CSS)
    this._input.style.height = "auto";
    this._input.style.height = `${this._input.scrollHeight}px`;
    this._clearError();
  }

  _onSend() {
    const text = this._input.value.trim();
    if (!text || this.hasAttribute("loading")) return;

    this._input.value = "";
    this._input.style.height = "auto";
    this._clearError();

    const userMsgEl = this._appendMessage("user", text);
    this._history.push({ role: "user", content: text });

    this.dispatchEvent(new CustomEvent("message-send", {
      detail: { text },
      bubbles: true,
      composed: true
    }));

    this._callEndpoint(text, userMsgEl);
  }

  _formatTime() {
    return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }

  _appendMessage(role, text) {
    const item = document.createElement("div");
    item.className = `msg msg--${role}`;
    item.setAttribute("part", `message message--${role}`);

    const bubble = document.createElement("div");
    bubble.className = "msg__bubble";
    bubble.textContent = text;

    const time = document.createElement("div");
    time.className = "msg__time";
    time.textContent = this._formatTime();
    time.setAttribute("aria-hidden", "true");

    item.appendChild(bubble);
    item.appendChild(time);
    this._thread.appendChild(item);
    this._scrollToBottom();
    return item;
  }

  _scrollToBottom() {
    this._thread.scrollTo({ top: this._thread.scrollHeight, behavior: "smooth" });
  }

  _setLoading(val) {
    if (val) {
      this.setAttribute("loading", "");
      this._input.disabled = true;
      this._sendBtn.disabled = true;
      this._scrollToBottom();
    } else {
      this.removeAttribute("loading");
      this._input.disabled = false;
      this._sendBtn.disabled = false;
    }
  }

  _showError(message) {
    this.setAttribute("error", "");
    this._errorText.textContent = message || "An error occurred. Please try again.";
  }

  _clearError() {
    this.removeAttribute("error");
    this._errorText.textContent = "";
  }

  async _callEndpoint(userText, userMsgEl) {
    const endpoint = this.getAttribute("endpoint");
    if (!endpoint) {
      this._showError("No endpoint configured.");
      return;
    }

    if (this._abortCtrl) this._abortCtrl.abort();
    this._abortCtrl = new AbortController();

    this._setLoading(true);

    let assistantMsgEl = null;
    let responseText = "";

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: userText,
          history: this._history.slice(0, -1) // exclude the message we just added
        }),
        signal: this._abortCtrl.signal
      });

      if (!response.ok) {
        const status = response.status;
        throw Object.assign(new Error(`Request failed: ${status}`), { status });
      }

      // Support both streaming (SSE / NDJSON) and single-shot JSON responses
      const contentType = response.headers.get("content-type") || "";

      if (contentType.includes("text/event-stream") || contentType.includes("application/x-ndjson")) {
        // Streaming response — append token-by-token
        assistantMsgEl = this._appendMessage("assistant", "");
        const bubbleEl = assistantMsgEl.querySelector(".msg__bubble");
        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        // Buffer to accumulate incomplete SSE lines split across chunk boundaries
        let lineBuffer = "";

        let done = false;
        while (!done) {
          const { value, done: doneReading } = await reader.read();
          done = doneReading;
          if (value) {
            // Prepend any buffered incomplete line from the previous chunk
            const chunk = lineBuffer + decoder.decode(value, { stream: !done });
            lineBuffer = "";

            const lines = chunk.split("\n");
            // If the chunk does not end with a newline the last element is an incomplete line —
            // save it to the buffer for the next iteration.
            if (!chunk.endsWith("\n")) {
              lineBuffer = lines.pop();
            }

            for (const line of lines) {
              if (!line.startsWith("data:")) continue;
              const raw = line.slice(5).trim();
              if (raw === "[DONE]") { done = true; break; }
              try {
                const parsed = JSON.parse(raw);
                // Expected streaming token shape (pick first matching field):
                //   { "text": "token" }    — preferred
                //   { "delta": "token" }   — OpenAI-compatible
                //   { "content": "token" } — Anthropic-compatible
                const token = parsed.text ?? parsed.delta ?? parsed.content ?? "";
                if (token) {
                  responseText += token;
                  bubbleEl.textContent = responseText;
                }
              } catch {
                // plain-text token (non-JSON SSE line)
                if (raw) {
                  responseText += raw;
                  bubbleEl.textContent = responseText;
                }
              }
              this._scrollToBottom();
            }
          }
        }

        // If the stream completed without yielding any visible content,
        // show a placeholder so the user doesn't see an empty bubble.
        if (!responseText || !responseText.trim()) {
          const placeholder = "[No content received]";
          responseText = placeholder;
          if (bubbleEl) {
            bubbleEl.textContent = placeholder;
          }
        }
      } else {
        // Non-streaming — expected JSON response shapes (first matching field wins):
        //   { "text": "..." }      — preferred / standard
        //   { "message": "..." }   — common alternative
        //   { "response": "..." }  — AWS Lambda default naming
        //   { "answer": "..." }    — Q&A service naming
        const data = await response.json();
        responseText = data.text ?? data.message ?? data.response ?? data.answer ?? "";
        assistantMsgEl = this._appendMessage("assistant", responseText);
      }

      this._history.push({ role: "assistant", content: responseText });

      this.dispatchEvent(new CustomEvent("response-receive", {
        detail: { text: responseText },
        bubbles: true,
        composed: true
      }));
    } catch (err) {
      if (err.name === "AbortError") {
        // Remove any partial streaming message from the UI to keep UI and
        // conversation history in sync — the user never received a full answer.
        if (assistantMsgEl && assistantMsgEl.parentNode) {
          assistantMsgEl.parentNode.removeChild(assistantMsgEl);
        }
        return;
      }

      // On error, remove the user message that was already added to history
      // so a retry doesn't send the failed turn twice as context.
      if (this._history.length && this._history[this._history.length - 1].role === "user") {
        this._history.pop();
      }
      // Also remove the user's message bubble from the UI so it matches history,
      // and keep it visible instead by re-populating the input so the user can retry.
      if (userMsgEl && userMsgEl.parentNode) {
        userMsgEl.parentNode.removeChild(userMsgEl);
        this._input.value = userText;
        this._input.style.height = "auto";
        this._input.style.height = `${this._input.scrollHeight}px`;
      }

      const status = err.status ?? 0;
      const message = status === 429
        ? "Too many requests. Please wait a moment."
        : status >= 500
          ? "The AI service is unavailable. Please try again later."
          : err.message || "An error occurred. Please try again.";

      this._showError(message);

      this.dispatchEvent(new CustomEvent("chat-error", {
        detail: { message, status },
        bubbles: true,
        composed: true
      }));
    } finally {
      this._setLoading(false);
      this._abortCtrl = null;
    }
  }

  // ── Public API ───────────────────────────────────────────────────────

  /** Open the chat panel */
  open() {
    if (this.hasAttribute("open")) return;
    this.setAttribute("open", "");
    this.dispatchEvent(new CustomEvent("chat-open", { bubbles: true, composed: true }));
  }

  /** Close the chat panel */
  close() {
    if (!this.hasAttribute("open")) return;
    this.removeAttribute("open");
    this.dispatchEvent(new CustomEvent("chat-close", { bubbles: true, composed: true }));
  }

  /** Toggle the chat panel */
  toggle() {
    if (this.hasAttribute("open")) {
      this.close();
    } else {
      this.open();
    }
  }

  /** Clear conversation history and message display */
  clearHistory() {
    this._history = [];
    while (this._thread.firstChild) {
      this._thread.removeChild(this._thread.firstChild);
    }
    this._clearError();
    const greeting = this.getAttribute("greeting");
    if (greeting) {
      this._appendMessage("assistant", greeting);
      this._history.push({ role: "assistant", content: greeting });
    }
  }
}

if (!customElements.get("ctl-ask-ai")) {
  customElements.define("ctl-ask-ai", CtlAskAi);
}

export default CtlAskAi;
